import { ElMessage } from "element-plus";
import axios, { type AxiosRequestConfig } from "axios";
import { getRequest } from "~/apis/requests/useApiCall";
import { useAuthStore } from "~/stores/auth";

// Isolated HTTP client for support API calls.
// This must not inherit cookie-based auth behavior from global axios defaults.
const supportHttp = axios.create({
  withCredentials: false,
});

type SupportToken = {
  token: string;
  expiry: number;
};

type SupportTicketMeta = {
  id: number;
  owner_id: number;
  title: string;
  status: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type SupportAttachment = {
  type: "image" | "video" | "audio" | "file";
  payload: {
    url: string;
  };
};

type SupportConversation = {
  id: number;
  ticket_id: number;
  creator_type: string;
  creator_id: number;
  creator_name: string;
  body: string;
  attachments: SupportAttachment[];
  reply_to_id: number | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

type SupportTicketDetails = SupportTicketMeta & {
  conversations: SupportConversation[];
};

type SupportTicketsResponse = {
  current_page: number;
  data: SupportTicketMeta[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
};

type CreateSupportTicketPayload = {
  title: string;
  body: string;
  metadata?: Record<string, unknown>;
  attachments?: File[];
};

/**
 * One thing waiting in the inbox.
 *
 * Three sources arrive in the same shape — a notice we were sent, a reply on a
 * ticket, a change to work that names this company — because to the person
 * looking at the widget they are one list with one count.
 */
/**
 * A picture, a video, or a YouTube embed on an announcement.
 *
 * File links are signed and expire, so they are minted fresh each time the
 * inbox is fetched rather than stored anywhere. A YouTube entry has no url —
 * nothing was uploaded — and carries the embed address instead.
 */
type SupportInboxMedia = {
  kind: "image" | "video" | "youtube";
  url?: string | null;
  embed_url?: string | null;
  video_id?: string | null;
  name?: string | null;
  mime?: string | null;
  size?: number | null;
  width?: number | null;
  height?: number | null;
};

type SupportInboxItem = {
  type: "announcement" | "message" | "ticket_reply" | "task_update";
  id: string;
  ref: number;
  title: string;
  body: string;
  /** Absent on anything older than the media release, hence optional. */
  media?: SupportInboxMedia[];
  level: "info" | "warning" | "critical" | "success";
  from: string;
  status?: string;
  at: string | null;
  unread: boolean;
  /**
   * The task activity behind a task update, and only on one.
   *
   * What a task update is marked read by. `ref` cannot do it: that is the task,
   * and one task can have several updates waiting, each read on its own. The
   * same number is in `id` as "activity:<id>", but parsing an id is guessing
   * at a format nobody promised to keep.
   *
   * Absent from an older server, which has no per-update read at all. The
   * Updates dialog takes its absence as the signal to fall back to the one
   * watermark per agent.
   */
  activity_id?: number;
};

/** A task, as its client is allowed to see it. */
type SupportTaskDetails = {
  id: number;
  code: string | null;
  title: string;
  description: string | null;
  status: string;
  resolved: boolean;
  priority: string | null;
  team: string | null;
  types: string[];
  due_date: string | null;
  created_at: string | null;
  completed_at: string | null;
  subtasks: { title: string; status: string; done: boolean }[];
  attachments: {
    name: string;
    url: string;
    mime: string | null;
    size: string | null;
  }[];
  comments: { body: string; by: string; at: string | null }[];
};

/** One message in the conversation with support. */
/**
 * A file, always with a name and always with its bytes in hand.
 *
 * Two things go wrong on a phone. A photograph picked on iOS can arrive with an
 * empty name, and appending it without one drops the part entirely — the server
 * then reports the file as missing while the person is looking straight at it.
 * And a File from an iOS picker is a handle to something the system still owns,
 * which iOS may release while the message is being typed; the handle then reads
 * as empty and the same thing happens.
 *
 * So the contents are read once, while the file is certainly still readable,
 * and posted as a copy under a name that is never blank.
 */
const appendFile = async (form: FormData, field: string, file: File) => {
  const name =
    file.name ||
    `upload-${Date.now()}.${(file.type || "").split("/")[1]?.replace("jpeg", "jpg") || "dat"}`;

  try {
    const bytes = new Blob([await file.arrayBuffer()], {
      type: file.type || "application/octet-stream",
    });

    form.append(field, bytes, name);
  } catch (error) {
    // Unreadable here is no worse than before: post the handle as it was.
    form.append(field, file, name);
  }
};

type SupportChatMessage = {
  id: number;
  ticket_id: number;
  body: string | null;
  attachments: any[];
  // A meeting shared into the conversation, when the message carries one.
  meeting: {
    url: string;
    starts_at: string | null;
    minutes: number;
  } | null;
  mine: boolean;
  from: string;
  from_staff: boolean;
  read_at: string | null;
  created_at: string | null;

  /*
   * Support can now correct or withdraw what it said.
   *
   * A withdrawn message keeps its place and loses its words — the billing app
   * sends the body already replaced, so an older build of this widget shows the
   * right thing without understanding any of these flags.
   */
  deleted?: boolean;
  edited?: boolean;
  edited_at?: string | null;

  // What it answers, quoted. The original may be far up the thread.
  reply_to?: {
    id: number;
    sender: string;
    body: string;
  } | null;
};

type SupportChatResponse = {
  ticket: {
    id: number;
    title: string;
    status: string;
    resolved: boolean;
    picked_by: string | null;
    last_message_at: string | null;
    created_at: string | null;
  } | null;
  messages: SupportChatMessage[];
  next_cursor: number | null;
  has_more: boolean;
  history: any[];
  // What the tickets in this page are, for the line between them.
  tickets: Record<
    string,
    {
      id: number;
      title: string;
      resolved: boolean;
      resolved_at: string | null;
      resolved_by: string | null;
      created_at: string | null;
    }
  >;
  unread: number;
  updates_unread: number;
};

type SupportInboxResponse = {
  unread: number;
  /**
   * Announcements alone.
   *
   * Separate from `unread`, which also counts task updates — and those cannot
   * be cleared by reading an announcement, so opening the carousel on the
   * mixed number would show a dialog with nothing new in it. Optional because
   * an older server does not send it.
   */
  announcements_unread?: number;
  items: SupportInboxItem[];
  agent: { id: number; name: string };
};

/**
 * One page of the whole history, for the What's New page.
 *
 * The items are SupportInboxItem, unchanged, so the same components draw a
 * card here and a page in the Updates dialog.
 *
 * ANNOUNCEMENTS AND WORK ON THE ACCOUNT, both. Every request below used to pin
 * `kind` to announcements, which left a client's own tasks off the one page
 * that claims to hold everything — and "why are the tasks not showing here?"
 * is not a question a page should make anybody ask. The page chooses the kind
 * now, and asks for the whole feed unless somebody narrows it.
 */
type SupportAnnouncementsMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

/**
 * Which kinds of update a request asks for.
 *
 * `all` is the whole feed. The What's New page offers All, Announcements and
 * Work, so `message` is a kind nothing here asks for by name: a direct message
 * is written to one person and answered in the conversation rather than read
 * back as history. One can still arrive under `all`, which is the whole feed
 * and says so, and the page labels it like any other card when it does.
 */
type SupportUpdatesKind = "all" | "announcement" | "message" | "task_update";

/**
 * The totals the cards at the top of the page report.
 *
 * One number per kind, plus everything and everything unread. The server takes
 * them under the same `kind` the request carries, so a request for Work is
 * answered with Work's counts alone — which is why fetchWhatsNew keeps these
 * only from a whole-feed request. The cards say what the account HOLDS, and
 * must not empty out when the list beneath them is narrowed.
 *
 * Within a kind they are taken across everything visible to the agent and are
 * deliberately blind to the search box and the Unread toggle, so they hold
 * still while somebody narrows the list underneath them. A card that moved
 * every time somebody typed would be measuring the search box rather than the
 * account.
 *
 * Which is also why the page must never count the array it holds: `total` is
 * every update ever sent, and the page holds one page of them.
 */
type SupportAnnouncementsCounts = {
  total: number;
  announcements: number;
  messages: number;
  task_updates: number;
  unread: number;
};

/**
 * `kind` is the caller's again. requestWhatsNew defaults it to the whole feed
 * rather than pinning it to one kind.
 */
type SupportAnnouncementsQuery = {
  page?: number;
  per_page?: number;
  q?: string;
  unread?: boolean;
  kind?: SupportUpdatesKind;
};

type SupportAnnouncementsResponse = {
  data: SupportInboxItem[];
  meta: SupportAnnouncementsMeta;
  counts: SupportAnnouncementsCounts;
};

/**
 * What the server said, with anything missing filled in sensibly.
 *
 * These clients deploy one at a time, so this frontend can be talking to an
 * API that does not send `meta` yet. A missing last_page reads as 1, which
 * means "no more pages" — the Load more button hides rather than asking for a
 * page two that does not exist.
 */
const asMeta = (
  meta: Partial<SupportAnnouncementsMeta> | undefined,
  fallbackPerPage: number,
  itemCount: number,
): SupportAnnouncementsMeta => ({
  current_page: Number(meta?.current_page ?? 1),
  last_page: Number(meta?.last_page ?? 1),
  per_page: Number(meta?.per_page ?? fallbackPerPage),
  total: Number(meta?.total ?? itemCount),
});

/**
 * The same filling-in for the counts block.
 *
 * `announcements` falls back to `total` for the same deploy-skew reason as
 * above: a server older than the mixed feed counted announcements and nothing
 * else, so its `total` IS the announcement total and reading it as one is
 * right rather than a guess. Work falls back to nothing, because an older
 * server has no number for it and 0 is the truthful thing to show.
 */
const asCounts = (
  counts: Partial<SupportAnnouncementsCounts> | undefined,
): SupportAnnouncementsCounts => ({
  total: Number(counts?.total ?? 0),
  announcements: Number(counts?.announcements ?? counts?.total ?? 0),
  messages: Number(counts?.messages ?? 0),
  task_updates: Number(counts?.task_updates ?? 0),
  unread: Number(counts?.unread ?? 0),
});

/**
 * How often the widget asks whether anything has arrived.
 *
 * Twenty seconds while the tab is in front of somebody, and nothing at all when
 * it is not: a support widget open in forty tabs across a call centre should
 * not be forty requests a second between them, and nobody is reading a
 * notification on a tab they cannot see.
 */
const POLL_VISIBLE_MS = 20_000;
const POLL_OPEN_MS = 6_000;

const NUDGE_FALLBACK_MINUTES = 30;

/**
 * How long between offers of help, in minutes.
 *
 * Thirty in earnest. Settable from the environment because the only way to see
 * this working is to wait for it, and nobody is going to sit through half an
 * hour to find out whether a one-line change landed:
 *
 *     NUXT_PUBLIC_SUPPORT_NUDGE_MINUTES=1 pnpm run dev
 *
 * Read when it is asked for rather than when this module loads:
 * useRuntimeConfig() needs a Nuxt instance, and there is not one yet at import
 * time. Anything missing or nonsensical falls back to the half hour.
 */
function nudgeMinutes(): number {
  try {
    const configured = Number(useRuntimeConfig()?.public?.supportNudgeMinutes);

    return Number.isFinite(configured) && configured > 0
      ? configured
      : NUDGE_FALLBACK_MINUTES;
  } catch {
    return NUDGE_FALLBACK_MINUTES;
  }
}

export const useSupportStore = definePiniaStore("supportStore", {
  state: () => ({
    _token: null as SupportToken | null,
    _refresh: null as number | null,
    _tickets: [] as SupportTicketMeta[],
    _ticketsPagination: {
      current_page: 1,
      per_page: 20,
      total: 0,
      last_page: 1,
      next_page_url: null as string | null,
      prev_page_url: null as string | null,
    },
    _selectedTicket: null as SupportTicketDetails | null,
    _isLoadingTickets: false,
    _isLoadingMoreTickets: false,
    _isCreatingTicket: false,
    _isLoadingTicketDetails: false,

    _inbox: [] as SupportInboxItem[],
    _unread: 0,
    _isLoadingInbox: false,
    _isReplying: false,
    // Held so polling can be stopped when the widget goes away, and so two
    // timers can never run at once.
    _pollTimer: null as ReturnType<typeof setInterval> | null,
    _tick: null as (() => Promise<void>) | null,
    /*
     * The newest message id the last poll saw, and the updates count with it.
     *
     * null until the first answer: that one is only a baseline, because opening
     * a page to find two messages waiting is not two messages arriving.
     */
    _lastSeenId: null as number | null,
    _lastSeenUpdates: null as number | null,
    // Whether a poll is already in flight. Two overlapping ones both read the
    // same last-seen id before either writes it, so one arrival sounds twice.
    _polling: false,
    /*
     * Whether the socket is carrying support events.
     *
     * While it is, the widget asks for nothing: everything it would have asked
     * about has already been pushed to it. When the socket drops, the timer
     * below takes over until it comes back.
     */
    _live: false,
    /*
     * Whether the conversation is on screen.
     *
     * Kept here rather than read from the component when a poll comes back. A
     * poll is answered by whatever timer is running, and asking a component
     * instance about itself at that moment means trusting that the instance
     * asked is the one somebody is looking at.
     */
    _panelOpen: false,
    _chat: [] as SupportChatMessage[],
    _chatTicket: null as SupportChatResponse["ticket"],
    _chatCursor: null as number | null,
    _chatHasMore: false,
    _history: [] as any[],
    _chatTickets: {} as SupportChatResponse["tickets"],
    _chatUnread: 0,
    _isLoadingChat: false,
    _isLoadingOlder: false,
    _isSending: false,
    /*
     * Whether the updates list is on screen.
     *
     * Held here rather than in a component because two of them ask for it: the
     * dialog itself, and the widget's Updates button, which no longer draws the
     * list — it only says to show it.
     */
    _updatesOpen: false,
    /** Unread announcements only — the number the carousel opens on. */
    _announcementsUnread: 0,
    /*
     * Reads on their way to the server: announcement refs, and task activity
     * ids.
     *
     * The Updates dialog can ask for the same page twice — once as it opens and
     * once as its pages arrive — and a GET answered before a read has been
     * written hands the item back unread. Held so neither sends a second
     * request, and so that GET cannot undo a read that is still in flight.
     */
    _announcementReadsInFlight: [] as number[],
    _activityReadsInFlight: [] as number[],
    /*
     * The What's New page: the whole feed, paged and searched.
     *
     * Kept apart from the inbox above on purpose. That one is a capped window
     * the dialog walks a page at a time, marking each read as it is shown; this
     * is the whole history, and the two must never share a list — a search for
     * "billing" landing in `_inbox` would leave the dialog paging through
     * somebody's search results and marking them read.
     */
    _whatsNew: [] as SupportInboxItem[],
    _whatsNewMeta: {
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: 0,
    } as SupportAnnouncementsMeta,
    _whatsNewCounts: {
      total: 0,
      announcements: 0,
      messages: 0,
      task_updates: 0,
      unread: 0,
    } as SupportAnnouncementsCounts,
    /*
     * Whether a whole-feed answer has ever filled the counts above.
     *
     * The cards at the top of the page are mounted from the first paint so they
     * animate in once and then update in place, which means they are on screen
     * before there is anything true to put in them. They show an em dash until
     * this is set: a 0 sitting where "412" is about to appear is a wrong
     * number, not an empty one.
     *
     * It is specifically about the COUNTS rather than about the list, because
     * only a whole-feed request brings them — somebody who switches to Work
     * before the first answer lands has a list to show and, until that answer
     * arrives, no counts to put beside it.
     */
    _whatsNewCountsLoaded: false,
    _isLoadingWhatsNew: false,
    _isLoadingMoreWhatsNew: false,
    /** Whether the last attempt failed, so the page can offer to try again. */
    _whatsNewFailed: false,
    /*
     * Which request the list on screen belongs to.
     *
     * Typing in the search box asks again on every pause, and the answers do
     * not come back in the order they were asked for — "bi" can be answered
     * after "billing" and would then replace it, so the list would settle on
     * results for a word nobody has finished typing. Every fetch that replaces
     * the list takes the next number, and only the holder of the current one is
     * allowed to write.
     */
    _whatsNewRequest: 0,
    /*
     * Which request the COUNTS belong to, numbered apart from the list.
     *
     * Only a request for the whole feed brings counts worth keeping, and those
     * are worth keeping even when the list they arrived with has been
     * overtaken: the counts are blind to the search box and the Unread toggle,
     * so they are equally true whichever list won, and the request that
     * overtook this one is usually a narrower kind that cannot bring them at
     * all. Sharing the list's number threw them away with the list — open the
     * page, tap Work before the opening answer lands, and the only whole-feed
     * answer of the session was dropped whole, leaving the cards on em dashes
     * for the rest of the visit.
     *
     * Only whole-feed requests take a number here, because only they may
     * write. A narrower one taking a number would make a whole-feed answer
     * still in the air look stale and lose the totals it was carrying.
     */
    _whatsNewCountsRequest: 0,
    /*
     * The question the list on screen is the answer to.
     *
     * Kept here rather than read back off the page when Load more is pressed,
     * because for as long as the search box is waiting out its pause those two
     * are different questions. Type a word and press Load more before the
     * pause is up: the page's live query is already the new search, while the
     * list and the page number beside it still belong to the old one, so page
     * two of the search would be appended to the unfiltered list. Paging what
     * is recorded here means the next page always continues the list it is
     * being added to.
     */
    _whatsNewQuery: {} as SupportAnnouncementsQuery,
    /*
     * When the widget may next offer itself.
     *
     * A timestamp rather than a countdown, so reloading the page does not put
     * the offer back at the front of the queue — somebody who dismissed it two
     * minutes ago should not see it again on the next screen they open.
     */
    _nudgeAt: 0,
    _nudgeDue: false,
    _nudgeTimer: null as any,
    _task: null as SupportTaskDetails | null,
    _isLoadingTask: false,
    // What the badge said last time we looked, so an arrival can be told apart
    // from a number that merely stayed the same.
    _lastSeenUnread: 0,
    /*
     * Which signed-in person this token was minted for.
     *
     * The token is persisted so a reload does not have to ask for a new one,
     * and it is opaque — nothing in it says who it belongs to. So whoever signs
     * in next inherits it, and CC Billing answers as the person before them:
     * their unread counts, and their conversation with support.
     */
    _tokenUserId: null as number | null,
  }),
  persist: {
    paths: ["_token", "_refresh", "_tokenUserId", "_nudgeAt"],
  },
  getters: {
    token: (state) => state._token,
    refresh: (state) => state._refresh,
    tickets: (state) => state._tickets,
    ticketsPagination: (state) => state._ticketsPagination,
    selectedTicket: (state) => state._selectedTicket,
    isLoadingTickets: (state) => state._isLoadingTickets,
    isLoadingMoreTickets: (state) => state._isLoadingMoreTickets,
    isCreatingTicket: (state) => state._isCreatingTicket,
    isLoadingTicketDetails: (state) => state._isLoadingTicketDetails,
    inbox: (state) => state._inbox,
    unread: (state) => state._unread,
    isLoadingInbox: (state) => state._isLoadingInbox,
    isReplying: (state) => state._isReplying,
    chat: (state) => state._chat,
    chatTicket: (state) => state._chatTicket,
    chatHasMore: (state) => state._chatHasMore,
    history: (state) => state._history,
    chatTickets: (state) => state._chatTickets,
    chatUnread: (state) => state._chatUnread,

    /*
     * What the closed widget shows.
     *
     * Both kinds of waiting at once: a reply nobody has read and an
     * announcement nobody has read are each a reason to open it, and a badge
     * that counts only one of them hides the other until somebody happens to
     * look.
     */
    badgeCount: (state) => state._chatUnread + state._unread,
    isLoadingChat: (state) => state._isLoadingChat,
    isLoadingOlder: (state) => state._isLoadingOlder,
    isSending: (state) => state._isSending,
    updatesOpen: (state) => state._updatesOpen,
    announcementsUnread: (state) => state._announcementsUnread,

    /**
     * Announcements and messages only, newest first.
     *
     * Task updates are left out so the Updates dialog can put them after these
     * rather than among them. Why that order matters is said where the dialog
     * builds its pages.
     */
    announcements: (state) =>
      state._inbox.filter(
        (item) => item.type === "announcement" || item.type === "message",
      ),

    whatsNew: (state) => state._whatsNew,
    whatsNewMeta: (state) => state._whatsNewMeta,
    whatsNewCounts: (state) => state._whatsNewCounts,
    whatsNewCountsLoaded: (state) => state._whatsNewCountsLoaded,
    isLoadingWhatsNew: (state) => state._isLoadingWhatsNew,
    isLoadingMoreWhatsNew: (state) => state._isLoadingMoreWhatsNew,
    whatsNewFailed: (state) => state._whatsNewFailed,

    /** Whether there is another page to append. */
    whatsNewHasMore: (state) =>
      state._whatsNewMeta.current_page < state._whatsNewMeta.last_page,

    nudgeDue: (state) => state._nudgeDue,
    task: (state) => state._task,
    isLoadingTask: (state) => state._isLoadingTask,
  },
  actions: {
    getSupportApiBaseUrl() {
      const fallbackBase =
        "https://billing.callcenter.sigmasolutions.com.bd/api/";
      let base = fallbackBase;

      // Guard against contexts where Nuxt app instance is not yet available.
      try {
        base = useRuntimeConfig().public.SUPPORT_API_BASE_URL || fallbackBase;
      } catch {
        base = fallbackBase;
      }

      return base.endsWith("/") ? base : `${base}/`;
    },

    isTokenExpired() {
      if (!this._token?.expiry) {
        return true;
      }

      const refreshAt = this._refresh || this._token.expiry - 8 * 60 * 60;
      const currentUnix = Math.floor(Date.now() / 1000);
      return currentUnix >= refreshAt;
    },

    isTokenHardExpired() {
      if (!this._token?.expiry) {
        return true;
      }

      const currentUnix = Math.floor(Date.now() / 1000);
      return currentUnix >= this._token.expiry;
    },

    async getToken(forceRefresh = false) {
      const ownerId = useAuthStore().user?.id ?? null;
      const mine = this._tokenUserId === ownerId;

      if (!forceRefresh && mine && this._token?.token && !this.isTokenExpired()) {
        return this._token;
      }

      // Somebody else's token is not a token to fall back on either.
      const previousToken = mine ? this._token : null;

      try {
        const { response, error } = await getRequest<SupportToken>(
          "support/apiToken",
        );

        if (error) {
          throw error;
        }

        this._token = response.data;
        this._tokenUserId = ownerId;
        this._refresh = response.data.expiry - 8 * 60 * 60;
        return response.data;
      } catch (error: any) {
        // Before true token expiry (24h), fallback to the previous token if present.
        if (previousToken?.token && !this.isTokenHardExpired()) {
          return previousToken;
        }

        ElMessage.error(
          error?.response?.data?.message || "Failed to load support token",
        );
        throw error;
      }
    },

    async supportRequest<T>(
      path: string,
      config: AxiosRequestConfig = {} as AxiosRequestConfig,
    ) {
      const token = await this.getToken();
      const supportBaseUrl = this.getSupportApiBaseUrl();
      const url = new URL(path.replace(/^\/+/, ""), supportBaseUrl).toString();

      return await supportHttp.request<T>({
        ...config,
        url,
        withCredentials: false,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token.token}`,
          ...(config.headers || {}),
        },
      });
    },

    async fetchTickets(page = 1, append = false) {
      if (append) {
        this._isLoadingMoreTickets = true;
      } else {
        this._isLoadingTickets = true;
      }

      try {
        const response = await this.supportRequest<SupportTicketsResponse>(
          `support/tickets?page=${page}`,
          {
            method: "GET",
          },
        );

        if (append) {
          const merged = [...this._tickets, ...response.data.data];
          this._tickets = merged.filter(
            (ticket, index, arr) =>
              arr.findIndex((item) => item.id === ticket.id) === index,
          );
        } else {
          this._tickets = response.data.data;
        }

        this._ticketsPagination = {
          current_page: response.data.current_page,
          per_page: response.data.per_page,
          total: response.data.total,
          last_page: response.data.last_page,
          next_page_url: response.data.next_page_url,
          prev_page_url: response.data.prev_page_url,
        };

        return response.data;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.data?.message || "Failed to load support tickets",
        );
        throw error;
      } finally {
        if (append) {
          this._isLoadingMoreTickets = false;
        } else {
          this._isLoadingTickets = false;
        }
      }
    },

    async fetchTicketDetails(ticketId: number) {
      this._isLoadingTicketDetails = true;
      try {
        const response = await this.supportRequest<SupportTicketDetails>(
          `support/tickets/${ticketId}`,
          {
            method: "GET",
          },
        );

        this._selectedTicket = response.data;
        return response.data;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.data?.message || "Failed to load support ticket",
        );
        throw error;
      } finally {
        this._isLoadingTicketDetails = false;
      }
    },

    /* ── the conversation ───────────────────────────────────────── */

    /**
     * Open the conversation.
     *
     * One request brings the current thread, what came before it and the
     * counts — the widget opens on a conversation, not on a form, and it should
     * not need three round trips to draw one.
     */
    async openChat(quiet = false) {
      if (!quiet) {
        this._isLoadingChat = true;
      }

      try {
        const response = await this.supportRequest<SupportChatResponse>(
          "support/chat",
          { method: "GET" },
        );

        const data = response.data;
        const arrived =
          data.unread > this._chatUnread ||
          data.messages.length > this._chat.length;

        this._chat = data.messages ?? [];
        this._chatTicket = data.ticket;
        this._chatCursor = data.next_cursor;
        this._chatHasMore = data.has_more;
        this._history = data.history ?? [];
        this._chatTickets = data.tickets ?? {};
        this._chatUnread = data.unread ?? 0;
        this._unread = data.updates_unread ?? this._unread;

        return { arrived };
      } catch (error: any) {
        if (!quiet) {
          ElMessage.error(
            error?.response?.data?.message || "Could not open support",
          );
        }

        return { arrived: false };
      } finally {
        this._isLoadingChat = false;
      }
    },

    /**
     * Further back in the conversation.
     *
     * By id, never by offset: a message arriving while somebody scrolls would
     * shift every offset by one and quietly repeat or skip one.
     */
    async loadOlderChat() {
      if (this._isLoadingOlder || !this._chatHasMore || !this._chatCursor) {
        return;
      }

      const ticketId = this._chatTicket?.id;

      if (!ticketId) {
        return;
      }

      this._isLoadingOlder = true;

      try {
        const response = await this.supportRequest<SupportChatResponse>(
          `support/chat/${ticketId}/messages?before=${this._chatCursor}`,
          { method: "GET" },
        );

        this._chat = [...(response.data.messages ?? []), ...this._chat];
        // Older pages bring older tickets with them.
        this._chatTickets = {
          ...(response.data.tickets ?? {}),
          ...this._chatTickets,
        };
        this._chatCursor = response.data.next_cursor;
        this._chatHasMore = response.data.has_more;
      } catch {
        // Nothing to say: the thread simply does not grow.
      } finally {
        this._isLoadingOlder = false;
      }
    },

    /**
     * Say something.
     *
     * No title, no category. If there is no conversation open this starts one,
     * which is what the ticket underneath is for.
     */
    async sendChat(body: string, attachments: File[] = []) {
      if (this._isSending) {
        return null;
      }

      this._isSending = true;

      try {
        let data: FormData | Record<string, unknown>;
        let headers: Record<string, string>;

        if (attachments.length) {
          const form = new FormData();
          form.append("body", body);
          for (const file of attachments) {
            await appendFile(form, "attachments[]", file);
          }
          data = form;
          headers = { "Content-Type": "multipart/form-data" };
        } else {
          data = { body };
          headers = { "Content-Type": "application/json" };
        }

        const response = await this.supportRequest<{
          ticket: SupportChatResponse["ticket"];
          message: SupportChatMessage;
          started: boolean;
        }>("support/chat", { method: "POST", data, headers });

        this._chat = [...this._chat, response.data.message];
        this._chatTicket = response.data.ticket;

        return response.data;
      } catch (error: any) {
        ElMessage.error(error?.response?.data?.message || "That did not send");

        return null;
      } finally {
        this._isSending = false;
      }
    },

    /**
     * Something arrived over the socket instead of being asked for.
     *
     * The billing app tells the client's own backend, which broadcasts on the
     * channel this agent is already connected to. The same payload the poll
     * would have returned, so both doors lead to the same state — and every
     * event carries every count, so one arriving can never leave another badge
     * stale.
     *
     * @returns whether this was news, so the widget can sound and follow it down
     */
    /*
     * Support corrected or withdrew something we already have.
     *
     * Replaced by id rather than appended: applyLive() ignores a message it
     * already knows, so an edit arriving there would simply never appear, and
     * the agent would go on reading words that had been taken back.
     *
     * Unknown ids are ignored rather than added — an edit to a message from
     * further back than this thread is loaded is nothing to show here, and the
     * corrected version arrives with the page the next time it is opened.
     */
    applyChange(payload: { message?: SupportChatMessage | null }) {
      const message = payload.message ?? null;

      if (!message) {
        return;
      }

      this._chat = this._chat.map((held) =>
        held.id === message.id ? { ...held, ...message } : held,
      );
    },

    applyLive(payload: {
      message?: SupportChatMessage | null;
      unread?: number;
      updates_unread?: number;
      read_up_to_id?: number;
      latest_id?: number;
    }) {
      const message = payload.message ?? null;
      const known = message
        ? this._chat.some((held) => held.id === message.id)
        : true;

      if (message && !known) {
        this._chat = [...this._chat, message];
      }

      if (typeof payload.unread === "number") {
        this._chatUnread = payload.unread;
      }

      if (typeof payload.updates_unread === "number") {
        this._unread = payload.updates_unread;
      }

      this.applyReadUpTo(payload.read_up_to_id ?? 0);

      /*
       * Kept in step with the poll's cursor.
       *
       * If the socket drops and the poll takes over, it must not decide that
       * everything it has already seen is new — and the tone must not sound
       * twice for one message.
       */
      if (typeof payload.latest_id === "number") {
        this._lastSeenId = Math.max(this._lastSeenId ?? 0, payload.latest_id);
      }

      if (typeof payload.updates_unread === "number") {
        this._lastSeenUpdates = payload.updates_unread;
      }

      // On screen means read, the same rule the poll follows.
      if (this._panelOpen && this._chatUnread > 0) {
        this.markChatRead();
      }

      return { arrived: Boolean(message) && !known };
    },

    /**
     * Their side has read ours, up to this message.
     *
     * One id rather than a flag per message: everything of ours at or below it
     * has been seen. The poll needs this because it only carries messages that
     * are new — it says nothing about the ones the widget already holds, so
     * without it a bubble kept its single tick for as long as the widget stayed
     * open.
     */
    applyReadUpTo(readUpTo: number) {
      if (!readUpTo) {
        return;
      }

      this._chat = this._chat.map((message) =>
        message.mine && !message.read_at && message.id <= readUpTo
          ? { ...message, read_at: new Date().toISOString() }
          : message,
      );
    },

    /**
     * What has changed since the last tick.
     *
     * Asks only for what is newer than the last message we hold, so the usual
     * answer is empty and costs almost nothing. It carries both badge numbers
     * too, which is why the timer needs this one request rather than this plus
     * the inbox.
     */
    async pollChat() {
      /*
       * One at a time.
       *
       * The timer, the visibility change and opening the widget can all ask at
       * once. Two answers landing together each compared against the same
       * last-seen id, so both decided something had arrived and one reply made
       * two sounds — and two identical requests where one would do.
       */
      if (this._polling) {
        return { arrived: false, messages: 0 };
      }

      this._polling = true;

      const after = this._chat.length
        ? this._chat[this._chat.length - 1].id
        : 0;

      try {
        const response = await this.supportRequest<{
          messages: SupportChatMessage[];
          tickets: SupportChatResponse["tickets"];
          ticket: SupportChatResponse["ticket"];
          unread: number;
          updates_unread: number;
          latest_id: number;
          read_up_to_id: number;
        }>(`support/chat/updates?after=${after}`, { method: "GET" });

        const data = response.data;
        const fresh = (data.messages ?? []).filter(
          (message) => !this._chat.some((held) => held.id === message.id),
        );

        if (fresh.length) {
          this._chat = [...this._chat, ...fresh];
          this._chatTickets = { ...this._chatTickets, ...(data.tickets ?? {}) };
        }

        this._chatTicket = data.ticket;

        this._chatUnread = data.unread ?? 0;
        this._unread = data.updates_unread ?? 0;

        this.applyReadUpTo(data.read_up_to_id ?? 0);

        /*
         * Something arrived.
         *
         * Told by ids and not by counts. A count is not a signal: reading a
         * message lowers it between two polls, so the next arrival only brings
         * it back to where it already was and reads as nothing having happened —
         * which is why the only time this ever made a sound was a page refresh.
         * An id only ever goes up.
         *
         * The first answer is a baseline, so what was already waiting when the
         * page opened stays quiet.
         */
        const latestId = data.latest_id ?? 0;
        const updates = data.updates_unread ?? 0;

        const arrived =
          (this._lastSeenId !== null && latestId > this._lastSeenId) ||
          (this._lastSeenUpdates !== null && updates > this._lastSeenUpdates);

        this._lastSeenId = latestId;
        this._lastSeenUpdates = updates;

        /*
         * On screen means read.
         *
         * Not "a new message arrived means read": that made clearing the count
         * depend on this poll having been the one to notice the message, and on
         * a callback into the component surviving to say so. If the conversation
         * is open and the server still counts something as unread, it has been
         * read — somebody is looking at it. Said every poll, so it corrects
         * itself however the message got here.
         */
        if (this._panelOpen && this._chatUnread > 0) {
          await this.markChatRead();
        }

        return { arrived, messages: fresh.length };
      } catch {
        // A badge is never worth an error on somebody's screen.
        return { arrived: false, messages: 0 };
      } finally {
        this._polling = false;
      }
    },

    /**
     * Their replies have been seen — all of them.
     *
     * Named no ticket. It used to mark the open one, but the count is taken
     * across the whole conversation: a reply in a ticket support had resolved
     * could never be marked read, and once the last ticket was resolved there
     * was no open ticket to name and the badge stuck for good.
     */
    async markChatRead() {
      if (this._chatUnread === 0) {
        return;
      }

      try {
        const response = await this.supportRequest<{ unread: number }>(
          "support/chat/read",
          { method: "POST", data: {} },
        );

        this._chatUnread = response.data.unread ?? 0;
      } catch {
        // The count corrects itself on the next open.
      }
    },

    /* ── the inbox ──────────────────────────────────────────────── */

    /**
     * Everything waiting, and how much of it is new.
     *
     * One request rather than a count endpoint and a list endpoint, because
     * this runs on a timer in every open tab and two round trips would be twice
     * the traffic for the same answer.
     */
    async fetchInbox(quiet = false) {
      if (!quiet) {
        this._isLoadingInbox = true;
      }

      try {
        const response = await this.supportRequest<SupportInboxResponse>(
          "support/inbox",
          { method: "GET" },
        );

        const arrived = response.data.unread > this._lastSeenUnread;

        /*
         * A read still on its way stays read.
         *
         * This GET can be answered before the POST that marked something has
         * been written, and would then hand the item back as unread — so the
         * Updates dialog, reopened in that moment, would land again on the page
         * it has just shown. Once the POST has settled, the next fetch is the
         * server's answer alone.
         */
        const readInFlight = (item: SupportInboxItem) =>
          ((item.type === "announcement" || item.type === "message") &&
            this._announcementReadsInFlight.includes(item.ref)) ||
          (item.type === "task_update" &&
            typeof item.activity_id === "number" &&
            this._activityReadsInFlight.includes(item.activity_id));

        this._inbox = (response.data.items ?? []).map((item) =>
          item.unread && readInFlight(item) ? { ...item, unread: false } : item,
        );
        this._unread = response.data.unread ?? 0;
        this._lastSeenUnread = this._unread;

        /*
         * Fall back to counting locally when the server does not say.
         *
         * These clients deploy one at a time, so a frontend can be newer than
         * the API it talks to. Counting the items we already hold gives the
         * same answer for anything on the first page.
         */
        this._announcementsUnread =
          response.data.announcements_unread ??
          this._inbox.filter(
            (item) =>
              item.unread &&
              (item.type === "announcement" || item.type === "message"),
          ).length;

        return {
          ok: true,
          arrived,
          unread: this._unread,
          announcementsUnread: this._announcementsUnread,
        };
      } catch (error: any) {
        // A background poll must never put an error in front of somebody who
        // is working; the widget simply stays as it was.
        if (!quiet) {
          ElMessage.error(
            error?.response?.data?.message || "Failed to load your inbox",
          );
        }

        /*
         * `ok` so a caller can tell "nothing is waiting" from "we never got to
         * ask". The updates dialog decides whether to open itself from this,
         * and a failed first fetch used to be indistinguishable from an empty
         * inbox — so a token that was not ready yet meant the dialog silently
         * never appeared for the rest of the session.
         */
        return {
          ok: false,
          arrived: false,
          unread: this._unread,
          announcementsUnread: this._announcementsUnread,
        };
      } finally {
        this._isLoadingInbox = false;
      }
    },

    /**
     * Say it has been seen — ALL of it.
     *
     * No current callers, and think hard before adding one. This posts an empty
     * body, and the server reads a missing `announcement_ids` key as "every
     * announcement visible to this agent", including any the capped inbox
     * window never returned. It is the right call for "I have opened my inbox
     * and dismissed the lot" and the wrong call for anything narrower — use
     * markAnnouncementRead or markTaskUpdateRead for one, or
     * markTaskUpdatesRead for the watermark.
     *
     * Called when the inbox is opened, which is what reading means. The server
     * answers with the new count rather than this guessing at zero — a ticket
     * reply that is still unanswered stays unread, and only the server knows
     * that.
     */
    async markInboxRead() {
      try {
        const response = await this.supportRequest<{ unread: number }>(
          "support/inbox/read",
          { method: "POST", data: {} },
        );

        this._unread = response.data.unread ?? 0;
        this._lastSeenUnread = this._unread;
        this._inbox = this._inbox.map((item) => ({ ...item, unread: false }));
      } catch {
        // Cosmetic. The count corrects itself on the next poll.
      }
    },

    /**
     * Mark one announcement read, and only that one.
     *
     * The whole point of reading them one at a time: closing the dialog after
     * the first must leave the second unread, so a refresh brings it back. The
     * blanket call above would defeat that by clearing everything the moment
     * anything was shown.
     *
     * The server distinguishes the two by whether ids are sent — with ids it
     * writes only those read rows and leaves the agent's inbox watermark
     * alone, which is what keeps task updates unread as well.
     */
    async markAnnouncementRead(ref: number) {
      /*
       * Locally first, so Next moves immediately.
       *
       * The count is corrected by the response, and again by the next poll —
       * pollChat overwrites _unread from the server every twenty seconds, so
       * an optimistic number here is never load-bearing for long.
       */
      this._inbox = this._inbox.map((item) =>
        item.ref === ref && (item.type === "announcement" || item.type === "message")
          ? { ...item, unread: false }
          : item,
      );

      /*
       * Once is enough.
       *
       * The local edit above is still made, because that is what corrects a
       * fetch that handed the item back unread while this was in flight. The
       * request is not repeated.
       */
      if (this._announcementReadsInFlight.includes(ref)) {
        return null;
      }

      this._announcementReadsInFlight = [
        ...this._announcementReadsInFlight,
        ref,
      ];

      try {
        const response = await this.supportRequest<{ unread: number }>(
          "support/inbox/read",
          { method: "POST", data: { announcement_ids: [ref] } },
        );

        this._unread = response.data.unread ?? 0;
        this._lastSeenUnread = this._unread;
        this._announcementsUnread =
          (response.data as { announcements_unread?: number }).announcements_unread ??
          Math.max(0, this._announcementsUnread - 1);

        return this._unread;
      } catch {
        // The local edit stands; the next poll settles the true count.
        return null;
      } finally {
        this._announcementReadsInFlight =
          this._announcementReadsInFlight.filter((held) => held !== ref);
      }
    },

    /**
     * Mark one task update read, and only that one.
     *
     * The counterpart of markAnnouncementRead for work on the account. It goes
     * by the activity — one update to a task — rather than by the task, because
     * one task can have several updates waiting and each is read on its own.
     * So closing the Updates dialog after the first leaves the second unread,
     * and a refresh opens on it.
     *
     * `announcement_ids` is sent, and sent empty, every time. An older server
     * does not know `task_update_ids`, and it reads a body with no
     * `announcement_ids` key as "mark every announcement read". With the empty
     * array it writes nothing at all. The dialog only has an activity id to
     * send when the server that sent the item knows what to do with it, so
     * that is a safety net rather than a path anybody should take.
     */
    async markTaskUpdateRead(activityId: number) {
      // Locally first, for the same reason as markAnnouncementRead.
      this._inbox = this._inbox.map((item) =>
        item.type === "task_update" && item.activity_id === activityId
          ? { ...item, unread: false }
          : item,
      );

      // Once is enough, as for an announcement.
      if (this._activityReadsInFlight.includes(activityId)) {
        return null;
      }

      this._activityReadsInFlight = [
        ...this._activityReadsInFlight,
        activityId,
      ];

      try {
        const response = await this.supportRequest<{
          unread: number;
          announcements_unread?: number;
        }>("support/inbox/read", {
          method: "POST",
          data: { announcement_ids: [], task_update_ids: [activityId] },
        });

        this._unread = response.data.unread ?? 0;
        this._lastSeenUnread = this._unread;
        // Reading a task update clears no announcement, so without a number
        // from the server the one we hold is still right.
        this._announcementsUnread =
          response.data.announcements_unread ?? this._announcementsUnread;

        return this._unread;
      } catch {
        // The local edit stands; the next poll settles the true count.
        return null;
      } finally {
        this._activityReadsInFlight = this._activityReadsInFlight.filter(
          (held) => held !== activityId,
        );
      }
    },

    /**
     * Move the task watermark, and nothing else.
     *
     * The legacy path. A server that sends `activity_id` on its task updates
     * has them marked one at a time by markTaskUpdateRead; this is for one that
     * does not, where the watermark is the only read a task update has. The
     * Updates dialog calls it from its last page, and only in that case.
     *
     * The blanket markInboxRead() above cannot be used for this. It posts an
     * empty body, so the server sees no `announcement_ids` key at all and takes
     * that as "mark every announcement visible to this agent read" — including
     * ones the inbox window never returned. Sending an EMPTY array instead is a
     * different instruction: the server writes no announcement rows and only
     * `task_updates: true` moves `inbox_read_at`.
     *
     * That distinction is deliberate on the server (SupportInboxController
     * separates `exists('announcement_ids')` from a null body precisely so a
     * client cannot wipe the lot by accident), and this is the client half of
     * it.
     */
    async markTaskUpdatesRead() {
      this._inbox = this._inbox.map((item) =>
        item.type === "task_update" ? { ...item, unread: false } : item,
      );

      try {
        const response = await this.supportRequest<{ unread: number }>(
          "support/inbox/read",
          { method: "POST", data: { announcement_ids: [], task_updates: true } },
        );

        this._unread = response.data.unread ?? 0;
        this._lastSeenUnread = this._unread;

        return this._unread;
      } catch {
        // The local edit stands; the next poll settles the true count.
        return null;
      }
    },

    /* ── what's new ─────────────────────────────────────────────── */

    /**
     * Ask for one page of the feed.
     *
     * Internal to the two actions below, which is why it writes nothing:
     * whether an answer is still wanted is the caller's question, because only
     * the caller knows which request it was. It throws like any request; both
     * callers catch.
     */
    async requestWhatsNew(query: SupportAnnouncementsQuery = {}) {
      const params: Record<string, string | number> = {
        page: Math.max(query.page ?? 1, 1),
        // The server caps this at 50 as well; asked for politely here so a
        // mistake in a caller is not a 422 in front of somebody.
        per_page: Math.min(Math.max(query.per_page ?? 20, 1), 50),
        /*
         * The caller's, with the whole feed as the default.
         *
         * This was pinned to announcements, on the grounds that one place
         * building the request was the one place that could guarantee what the
         * page showed. What it guaranteed in practice was a page of everything
         * that has happened with the client's own tasks missing from it, and no
         * way to ask for them. The page chooses now: All, Announcements, or
         * Work.
         *
         * The server takes the counts block under this same parameter, which is
         * why fetchWhatsNew keeps the counts from a whole-feed request and
         * drops the ones that come back with a narrower kind.
         */
        kind: query.kind ?? "all",
      };

      const term = (query.q ?? "").trim();

      if (term) {
        params.q = term;
      }

      // Present only when it is on. The contract spells this one as the string
      // "1" and says nothing about what an off value means, so off is absence.
      if (query.unread) {
        params.unread = "1";
      }

      const response = await this.supportRequest<SupportAnnouncementsResponse>(
        "support/announcements",
        { method: "GET", params },
      );

      return response.data;
    },

    /**
     * A read still on its way stays read.
     *
     * The same correction fetchInbox makes, for the same reason: this GET can
     * be answered before the POST that marked something has been written, and
     * would then hand the item back unread — so a card somebody has just opened
     * would light up as new again under their cursor.
     *
     * Asked by kind, exactly as fetchInbox asks it. A ref is only unique within
     * a kind: announcement 41 and task 41 are different rows in different
     * tables that both answer to 41, and this list holds both now. Matching on
     * the number alone would clear the New pill from a task update because
     * somebody had just read an announcement that happened to share its id.
     */
    withPendingReads(items: SupportInboxItem[]) {
      return items.map((item) => {
        const pending =
          item.type === "task_update"
            ? typeof item.activity_id === "number" &&
              this._activityReadsInFlight.includes(item.activity_id)
            : this._announcementReadsInFlight.includes(item.ref);

        return item.unread && pending ? { ...item, unread: false } : item;
      });
    },

    /**
     * The first page of a new search, a new filter, or of the page opening.
     *
     * Never throws. The page has an error state with a Retry in it, and an
     * exception here would take the whole screen down for one failed request —
     * including the counts and the filters somebody had set.
     */
    async fetchWhatsNew(query: SupportAnnouncementsQuery = {}) {
      const mine = ++this._whatsNewRequest;

      // The counts' own number, taken only when this request is one that can
      // bring them. Why they are numbered apart from the list is said where
      // `_whatsNewCountsRequest` is declared.
      const wholeFeed = (query.kind ?? "all") === "all";
      const mineCounts = wholeFeed ? ++this._whatsNewCountsRequest : 0;

      /*
       * Recorded beside the number, because from here on this is the query the
       * list belongs to. Only the filters are kept: the page is meta's to
       * count, and per_page is taken from meta as well so a server that caps
       * it differently is still paged in its own units.
       */
      this._whatsNewQuery = {
        kind: query.kind,
        q: query.q,
        unread: query.unread,
      };

      this._isLoadingWhatsNew = true;
      this._whatsNewFailed = false;

      try {
        const data = await this.requestWhatsNew({ ...query, page: 1 });

        /*
         * The counts, and ONLY from a request for the whole feed.
         *
         * The server takes them under the `kind` it was asked for, so the
         * answer to a request for Work carries Work's counts and zeroes for
         * everything else. Writing those would empty the Announcements card the
         * moment somebody narrowed to Work — and the cards exist to say what
         * the account holds, which is the thing being narrowed. They must hold
         * still.
         *
         * The page opens on All, so a whole-feed answer always arrives; until
         * one has, the cards show an em dash rather than a wrong zero, which is
         * what `_whatsNewCountsLoaded` is for.
         *
         * `wholeFeed` reads as redundant — the page nearly always asks for
         * everything — and it is not. Removing it is exactly how the cards
         * start reporting the filter instead of the account.
         *
         * TAKEN ABOVE THE LIST'S STALENESS GUARD, on the counts' own number, so
         * that an answer whose list has been overtaken still hands over its
         * totals. That answer is dropped as a list and kept as counts: the two
         * halves have different owners, and the newest whole-feed answer owns
         * the counts however its list fared. Below the guard, somebody who
         * tapped Work while the opening request was in the air lost the only
         * whole-feed answer of the session — em dashes on the very screen they
         * opened to see how much work there is.
         */
        if (wholeFeed && mineCounts === this._whatsNewCountsRequest) {
          this._whatsNewCounts = asCounts(data?.counts);
          this._whatsNewCountsLoaded = true;
        }

        // Overtaken while this was in the air: a later search owns the list
        // now, and writing would put its results back.
        if (mine !== this._whatsNewRequest) {
          return { ok: true, stale: true };
        }

        const items = this.withPendingReads(data?.data ?? []);

        this._whatsNew = items;
        this._whatsNewMeta = asMeta(
          data?.meta,
          query.per_page ?? 20,
          items.length,
        );

        return { ok: true, stale: false };
      } catch {
        // A failure that has already been overtaken is not this list's
        // failure, and must not put an error state over a good list.
        if (mine !== this._whatsNewRequest) {
          return { ok: false, stale: true };
        }

        this._whatsNewFailed = true;

        return { ok: false, stale: false };
      } finally {
        if (mine === this._whatsNewRequest) {
          this._isLoadingWhatsNew = false;
        }
      }
    },

    /**
     * The next page, appended.
     *
     * Pages the query the list was built from, which fetchWhatsNew recorded
     * when it asked for page one — deliberately not a query handed in here.
     * The caller's filters and the list on screen disagree for as long as the
     * search box is waiting out its pause, and paging the caller's copy in
     * that window appends page two of one question to the answer to another.
     * Changing a filter goes through fetchWhatsNew instead, which starts a
     * fresh list at page one.
     *
     * A failure here sets no error state on purpose: there is a good list on
     * screen, and replacing it with an apology because page three did not
     * arrive would be worse than the button simply coming back to be pressed
     * again.
     */
    async loadMoreWhatsNew() {
      if (this._isLoadingMoreWhatsNew || this._isLoadingWhatsNew) {
        return { ok: true, stale: false };
      }

      const meta = this._whatsNewMeta;

      if (meta.current_page >= meta.last_page) {
        return { ok: true, stale: false };
      }

      // Not a new list, so it does not take a new number — but it is still
      // thrown away if a search starts while it is in the air.
      const mine = this._whatsNewRequest;

      this._isLoadingMoreWhatsNew = true;

      try {
        const data = await this.requestWhatsNew({
          ...this._whatsNewQuery,
          page: meta.current_page + 1,
          per_page: meta.per_page,
        });

        if (mine !== this._whatsNewRequest) {
          return { ok: true, stale: true };
        }

        /*
         * Merged by id rather than simply concatenated.
         *
         * The server orders by date, so anything sent between the two requests
         * shifts every later item down a place and hands page two an item page
         * one has already drawn. Two cards with the same key is a duplicate on
         * screen and a Vue warning in the console.
         */
        const merged = [
          ...this._whatsNew,
          ...this.withPendingReads(data?.data ?? []),
        ];

        this._whatsNew = merged.filter(
          (item, index, all) =>
            all.findIndex((held) => held.id === item.id) === index,
        );

        this._whatsNewMeta = asMeta(
          data?.meta,
          meta.per_page,
          this._whatsNew.length,
        );

        /*
         * The counts are deliberately left alone here.
         *
         * They are totals across everything the agent can see, so another page
         * of the same list carries no news about them — while taking the
         * server's copy anyway would undo a Mark as read whose POST has not
         * been written yet, putting the Unread card back up next to an item
         * that no longer shows a New chip. fetchWhatsNew is where they are
         * refreshed, because that is a fresh question rather than more of the
         * answer to this one.
         */

        return { ok: true, stale: false };
      } catch {
        return { ok: false, stale: false };
      } finally {
        this._isLoadingMoreWhatsNew = false;
      }
    },

    /**
     * One item on the What's New page has been read, whatever kind it is.
     *
     * The request is markAnnouncementRead's or markTaskUpdateRead's and not a
     * second copy of either. All this adds is keeping the page's own list and
     * its Unread card in step, because those actions know about the inbox and
     * nothing else.
     *
     * TAKEN BY ID, not by ref. Refs are only unique within a kind — announcement
     * 41 and task 41 are different rows that both answer to 41 — and this list
     * holds both kinds now, so the number alone no longer names one item.
     *
     * Nothing here marks anything on render. The dialog's one-at-a-time
     * behaviour depends on items staying unread until somebody has actually
     * looked at one, so this is only ever called from a deliberate act:
     * expanding a body, or pressing Mark as read.
     */
    async markWhatsNewRead(id: string) {
      const held = this._whatsNew.find((item) => item.id === id);

      if (!held || !held.unread) {
        return null;
      }

      /*
       * Which read this one takes, decided before anything is changed.
       *
       * An announcement or a message is marked by its ref. A task update is
       * marked by the activity behind it, because one task can have several
       * updates waiting and each is read on its own.
       *
       * A TASK UPDATE WITH NO ACTIVITY ID came from a server older than
       * per-update reads, where the only read one has is the agent's single
       * inbox watermark — markTaskUpdatesRead, which clears every task update
       * on the account at once. That is defensible in the Updates dialog, which
       * walks the whole sequence one page at a time and writes it from the last
       * page, so everything ahead of it has been shown. It is indefensible
       * here: this page shows the lot in any order and marks only what somebody
       * pressed, so the blanket call would clear updates nobody has looked at
       * and quietly empty the dialog that exists to show them. So it is left
       * unread, and the page draws no Mark as read on a card it cannot mark.
       */
      const activityId = held.type === "task_update" ? held.activity_id : null;

      if (held.type === "task_update" && typeof activityId !== "number") {
        return null;
      }

      /*
       * Whether somebody is already marking this one.
       *
       * Both mark actions answer null to two different questions: "the POST
       * failed" and "a POST for this one is already in flight, so I did not
       * send a second one". Only the first is a reason to put the card back,
       * and the Updates dialog marking the same item a moment earlier is an
       * ordinary way to reach the second. Asked before the call, because the
       * call itself adds this one to that list.
       */
      const alreadyMarking =
        typeof activityId === "number"
          ? this._activityReadsInFlight.includes(activityId)
          : this._announcementReadsInFlight.includes(held.ref);

      // By id, for the reason above: it is the only thing that names one item
      // across both kinds.
      this._whatsNew = this._whatsNew.map((item) =>
        item.id === held.id ? { ...item, unread: false } : item,
      );

      // Both of these are needed if it fails: the first is the number to put
      // back, and the second is how to tell that nothing else has written the
      // counts in the meantime.
      const countsBefore = this._whatsNewCounts;

      this._whatsNewCounts = {
        ...countsBefore,
        unread: Math.max(0, countsBefore.unread - 1),
      };

      const countsAfter = this._whatsNewCounts;

      const result =
        typeof activityId === "number"
          ? await this.markTaskUpdateRead(activityId)
          : await this.markAnnouncementRead(held.ref);

      /*
       * It did not get through, so put the card and the number back.
       *
       * The widget can afford to leave a failed read looking done, because
       * pollChat overwrites the count from the server every twenty seconds and
       * the item comes back unread with it. This page never polls: left alone,
       * the card would sit here looking read until somebody reloaded, while
       * the Updates dialog went on opening on that same announcement, still
       * unread, every morning.
       */
      if (result === null && !alreadyMarking) {
        this._whatsNew = this._whatsNew.map((item) =>
          item.id === held.id ? { ...item, unread: true } : item,
        );

        // Only when the counts are still the ones this action decremented. A
        // search landing while the POST was in the air brings a fresh total
        // from the server, which knows better than a number taken away here.
        if (this._whatsNewCounts === countsAfter) {
          this._whatsNewCounts = { ...countsBefore };
        }
      }

      return result;
    },

    /**
     * Keep asking, but only while somebody is looking.
     *
     * Polling a hidden tab wakes a laptop to fetch a notification nobody can
     * see, so the timer stops when the page is hidden and asks once immediately
     * when it comes back — which is also the moment a stale badge is most
     * obvious.
     */
    startInboxPolling(onArrival?: () => void, onMessages?: () => void) {
      if (this._pollTimer) {
        return;
      }

      const tick = async () => {
        if (document.visibilityState !== "visible") {
          return;
        }

        /*
         * Only while nothing is listening.
         *
         * Support now arrives on the socket the agent is already connected to,
         * so asking as well would be asking for what we have already been told —
         * one request per agent every few seconds, from every client, all day.
         * When that socket is down this is what keeps the widget working.
         */
        if (this._live) {
          return;
        }

        const { arrived, messages } = await this.pollChat();

        if (messages) {
          onMessages?.();
        }

        if (arrived) {
          onArrival?.();
        }
      };

      this._pollTimer = setInterval(tick, POLL_VISIBLE_MS);
      this._tick = tick;

      document.addEventListener("visibilitychange", this.onVisibilityChange);

      tick();
    },

    onVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.pollChat();
      }
    },

    /**
     * How often to ask, given whether anybody is looking.
     *
     * Six seconds with the conversation open, twenty with it shut. A reply
     * appearing twenty seconds after it was sent reads as broken to the person
     * waiting for it, and asking every six seconds all day for a badge nobody
     * is watching is rude to the server.
     */
    /**
     * Whether the socket is up, and one catch-up when it comes back.
     *
     * Anything that happened while it was down was said to nobody — a socket has
     * no replay — so coming back is exactly the moment to ask once rather than
     * to trust what is on screen.
     */
    setLive(live: boolean) {
      const wasDown = this._live === false;

      this._live = live;

      if (live && wasDown) {
        this.pollChat();
      }
    },

    setPollPace(open: boolean) {
      // Read before the pace, so the poll this kicks off already knows.
      this._panelOpen = open;

      if (!this._pollTimer || !this._tick) {
        return;
      }

      clearInterval(this._pollTimer);
      this._pollTimer = setInterval(
        this._tick,
        open ? POLL_OPEN_MS : POLL_VISIBLE_MS,
      );

      if (open) {
        this._tick();
      }
    },

    stopInboxPolling() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer);
        this._pollTimer = null;
      }

      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    },

    /**
     * The work behind an update.
     *
     * The inbox says something moved on this account; this answers "what?"
     * without sending anybody out of the app they are working in. CC Billing
     * decides what may be shown — only tasks that name this client, and only
     * comments marked public.
     */
    async fetchTask(taskId: number) {
      this._isLoadingTask = true;
      this._task = null;

      try {
        const response = await this.supportRequest<{
          data: SupportTaskDetails;
        }>(`support/tasks/${taskId}`, { method: "GET" });

        this._task = response.data.data ?? null;

        return this._task;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.status === 404
            ? "That one is not on your account."
            : error?.response?.data?.message || "Could not open that task",
        );

        return null;
      } finally {
        this._isLoadingTask = false;
      }
    },

    /**
     * Offer help now and then, to people who have not asked for it.
     *
     * Checked on a slow tick against a stored time rather than run off a
     * thirty-minute setInterval: a timer only counts while the tab is open, so
     * anybody moving between screens would never reach the end of one.
     *
     * Whether it is actually shown is the widget's decision — it knows about
     * recording and screenshots, and this does not.
     */
    startNudges(minutes = nudgeMinutes()) {
      if (this._nudgeTimer || !import.meta.client) {
        return;
      }

      const gap = Math.max(minutes, 0) * 60 * 1000;

      /*
       * Never further out than the interval asks for.
       *
       * The time is remembered across reloads, so shortening the interval
       * changed nothing until the old, longer wait had elapsed — which makes it
       * impossible to try a shorter one, and surprising when it is changed in
       * earnest.
       */
      if (!this._nudgeAt || this._nudgeAt > Date.now() + gap) {
        this._nudgeAt = Date.now() + gap;
      }

      /*
       * Checked often enough to be worth setting a short interval.
       *
       * A fixed thirty-second tick means a one-minute nudge can take ninety
       * seconds, so trying one out tells you very little. A tenth of the gap,
       * within a second and half a minute.
       */
      const tick = Math.min(Math.max(gap / 10, 1000), 30 * 1000);

      /*
       * A way to see it now, while developing.
       *
       * Even a one-minute interval is a minute per attempt, and this is a thing
       * you want to look at repeatedly — the wording, where it sits, what
       * dismissing it does. Only in dev, so it is not something that exists to
       * be found in a client's browser.
       *
       *     __nudgeNow()    show it
       *     __nudgeReset()  put it back to a full interval away
       */
      if (import.meta.dev) {
        (window as any).__nudgeNow = () => {
          this._nudgeAt = Date.now();
          this._nudgeDue = true;
        };

        (window as any).__nudgeReset = () => {
          this._nudgeDue = false;
          this._nudgeAt = Date.now() + gap;
        };
      }

      this._nudgeTimer = setInterval(() => {
        if (document.visibilityState !== "visible") {
          return;
        }

        if (!this._nudgeDue && Date.now() >= this._nudgeAt) {
          this._nudgeDue = true;
        }
      }, tick);
    },

    stopNudges() {
      if (this._nudgeTimer) {
        clearInterval(this._nudgeTimer);
        this._nudgeTimer = null;
      }
    },

    /** Seen, or waved away. Either way, not again for another interval. */
    snoozeNudge(minutes = nudgeMinutes()) {
      this._nudgeDue = false;
      this._nudgeAt = Date.now() + minutes * 60 * 1000;
    },

    openUpdates() {
      this._updatesOpen = true;
    },

    closeUpdates() {
      this._updatesOpen = false;
    },

    /**
     * Forget everything about the person who was signed in.
     *
     * Signing out resets the auth store but left this one alone, and the token
     * here outlives a session by design — so the next person to sign in on the
     * same browser carried on as the last one.
     */
    clearSession() {
      this.stopInboxPolling();
      this.$reset();
    },

    clearTask() {
      this._task = null;
    },

    /* ── answering ──────────────────────────────────────────────── */

    /**
     * Reply to a ticket.
     *
     * Until now the only way to add to a thread was to raise another ticket,
     * which is how one question becomes four tickets nobody can follow.
     */
    async replyToTicket(
      ticketId: number,
      body: string,
      attachments: File[] = [],
    ) {
      this._isReplying = true;

      try {
        let data: FormData | Record<string, unknown>;
        let headers: Record<string, string>;

        if (attachments.length) {
          const formData = new FormData();
          formData.append("body", body);
          for (const file of attachments) {
            await appendFile(formData, "attachments[]", file);
          }
          data = formData;
          headers = { "Content-Type": "multipart/form-data" };
        } else {
          data = { body };
          headers = { "Content-Type": "application/json" };
        }

        const response = await this.supportRequest<SupportConversation>(
          `support/tickets/${ticketId}/replies`,
          { method: "POST", data, headers },
        );

        // Shown straight away rather than after a refetch, so the thread reads
        // like a conversation instead of a form that clears.
        if (this._selectedTicket?.id === ticketId) {
          this._selectedTicket.conversations = [
            ...(this._selectedTicket.conversations ?? []),
            response.data,
          ];
        }

        return response.data;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.data?.message || "Your reply did not send",
        );
        throw error;
      } finally {
        this._isReplying = false;
      }
    },

    /** Say it is dealt with. */
    async resolveTicket(ticketId: number) {
      try {
        const response = await this.supportRequest<SupportTicketMeta>(
          `support/tickets/${ticketId}/resolve`,
          { method: "POST", data: {} },
        );

        if (this._selectedTicket?.id === ticketId) {
          this._selectedTicket.status = response.data.status;
        }

        this._tickets = this._tickets.map((ticket) =>
          ticket.id === ticketId
            ? { ...ticket, status: response.data.status }
            : ticket,
        );

        ElMessage.success("Marked as resolved. Replying reopens it.");

        return response.data;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.data?.message || "Could not resolve that ticket",
        );
        throw error;
      }
    },

    async createTicket(payload: CreateSupportTicketPayload) {
      this._isCreatingTicket = true;
      try {
        const hasAttachments = Boolean(payload.attachments?.length);

        let data: FormData | Record<string, unknown>;
        let headers: Record<string, string> | undefined;

        if (hasAttachments) {
          const formData = new FormData();
          formData.append("title", payload.title);
          formData.append("body", payload.body);
          if (payload.metadata) {
            formData.append("metadata", JSON.stringify(payload.metadata));
          }

          for (const file of payload.attachments ?? []) {
            await appendFile(formData, "attachments[]", file);
          }

          data = formData;
          headers = {
            "Content-Type": "multipart/form-data",
          };
        } else {
          data = {
            title: payload.title,
            body: payload.body,
            metadata: payload.metadata,
          };
          headers = {
            "Content-Type": "application/json",
          };
        }

        const response = await this.supportRequest<SupportTicketDetails>(
          "support/tickets",
          {
            method: "POST",
            data,
            headers,
          },
        );

        return response.data;
      } catch (error: any) {
        ElMessage.error(
          error?.response?.data?.message || "Failed to create support ticket",
        );
        throw error;
      } finally {
        this._isCreatingTicket = false;
      }
    },

    clearSelectedTicket() {
      this._selectedTicket = null;
    },
  },
});
